import {
  Component,
  ChangeDetectorRef,
  ElementRef,
  OnDestroy,
  OnInit,
  HostListener,
  ViewChild,
} from "@angular/core";
import { NavigationEnd, Router } from "@angular/router";
import { MatDialog } from "@angular/material/dialog";
import { TranslateService } from "@ngx-translate/core";
import { KeycloakService } from "keycloak-angular";
import { CompanyApiService } from "../../services/generated/waba-api/services";
import { ContactApiService } from "../../services/generated/waba-api/services/contact-api.service";
import { SharedLogoHeaderService } from "../../services/header/shared-logo-header.service";
import { AuthService } from "../../services/auth/auth.service";
import { initFlowbite } from "flowbite";
import { SidebarPanelService } from "../../services/sidebar/sidebar-panel.service";
import { MetaNotificationApiService } from "../../services/generated/waba-api/services";
import { MetaNotificationConfigApiService } from "../../services/generated/waba-api/services";
import { v4 as uuidv4 } from "uuid";
import { MetaNotification, MetaNotificationConfig } from "../../services/generated/waba-api/models";
import { filter, interval, Subscription } from "rxjs";
import { UpdateConfig$Params } from "../../services/generated/waba-api/fn/meta-notification-config-api/update-config";
import { LoaderService } from "../../shared/loader/service/loader.service";
import { PendingMessagesService } from "../../services/generated/waba-api/services";
import { DomSanitizer, SafeUrl } from "@angular/platform-browser";
import { ensureTranslateLangLoaded } from "../../shared/utils/ensure-translate-lang";
import { translateContactUploadReason } from "../../shared/utils/contact-upload-reason";
import { decodeInteractiveReplyCode } from "../../shared/utils/format-last-message.util";
import { ContactUploadBatchModalComponent } from "../../components/standalones/contact-upload-batch-modal/contact-upload-batch-modal.component";
import { Role } from "../../shared/utils/role.model";
import { AlertResult, AlertService } from "../../shared/alert/service/alert.service";
import { OnboardingTrackerService } from "../../services/onboarding/onboarding-tracker.service";
import { MyAvatarApiService } from "../../services/company/my-avatar-api.service";
import { APP_PATHS } from "../../shared/utils/app-route-paths";
import { SILENT_HTTP_CONTEXT } from "../../shared/utils/silent-http.util";
import { CompanyTimeService } from "../../shared/services/company-time.service";
import { importCounts, importNotice } from "../../shared/utils/import-notice.util";

/** The names the import services give an import the merchant did not name. */
const DEFAULT_IMPORT_NAMES = ["Products", "Rooms", "Activities", "Categories", "Contacts", "Records"];
import {
  HEADER_COMPANY_MENU,
  HEADER_OPTIONAL_STEPS,
  HEADER_QUICK_APPS,
  HEADER_SETUP_STEPS,
  HeaderSetupStep,
  HeaderToolbarLink,
  SETUP_STORAGE_KEY,
} from "./header-toolbar.config";

type NotificationType = NonNullable<MetaNotificationConfig["enabledTypes"]>[number];

const ALL_NOTIFICATION_TYPES = [
  "TEMPLATE_APPROVED",
  "TEMPLATE_REJECTED",
  "MESSAGE",
  "TEXT",
  "IMAGE",
  "VIDEO",
  "DOCUMENT",
  "AUDIO",
  "FLOW",
  "LOCATION",
  "CONTACTS",
  "REACTION",
  "ORDER",
  "TICKET_MENTION",
  "CONTACT_UPLOAD_BATCH_FINISHED",
  "IMPORT_JOB_FINISHED",
  "BROADCAST_EXPORT_FINISHED",
  "PENDING_MESSAGE_EXPORT_FINISHED",
  "PAYMENT_CAPTURE_REQUIRED",
  "DASHY_KPI_RECAP_DAILY",
  "DASHY_KPI_RECAP_WEEKLY",
  "DASHY_KPI_RECAP_MONTHLY",
] as const satisfies readonly NotificationType[];

const KPI_RECAP_NOTIFICATION_TYPES: readonly NotificationType[] = [
  "DASHY_KPI_RECAP_DAILY",
  "DASHY_KPI_RECAP_WEEKLY",
  "DASHY_KPI_RECAP_MONTHLY",
];

// Meta notification bell + polling — messaging group only, with inbox/broadcast roles.
const MESSAGING_NOTIFICATION_ROLES: readonly Role[] = [
  Role.MANAGE_CONTACT,
  Role.BROADCAST_ADD,
  Role.BROADCAST_VIEW,
  Role.TEAM_INBOX,
  Role.CONVERSATION_VIEW,
  Role.MANAGE_BOTS,
  Role.MANAGE_TICKET,
  Role.TICKET_VIEW,
  Role.CONSULT_NOTIFS,
];

const PAYMENT_NOTIFICATION_ROLES: readonly Role[] = [
  Role.MANAGE_PAYMENT_AUTHZ,
  Role.PAYMENT_VIEW,
];

@Component({
  selector: "app-header",
  templateUrl: "./header.component.html",
  styleUrl: "./header.component.scss",
})
export class HeaderComponent implements OnInit, OnDestroy {
  protected readonly Role = Role;
  @ViewChild("notificationToggle") notificationToggle?: ElementRef<HTMLElement>;

  currentLanguage = "fr";
  companyLogoUrl: string | undefined;
  companyName = "";
  companyBrandColor = "#00908e";
  readonly appLogoImage = "assets/logo.png";
  private ownedLogoUrl: string | null = null;
  private logoSubscription?: Subscription;

  notifications: any[] = [];
  page: number = 0;
  size: number = 10;
  totalElements: number = 0;
  unreadCount: number = 0;
  actionRequiredCount: number = 0;
  paymentAction?: MetaNotification;
  unreadOnly: boolean = false;
  showNotifications: boolean = false;
  hasWhatsAppAccount: boolean = false;
  notificationDropdownStyle: Record<string, string> = {};
  loadingNotifications: boolean = false;
  markingAllAsRead: boolean = false;
  pollingSubscription: Subscription | undefined;
  /** Completion notifications that existed when this header started, plus ones already handled. */
  private readonly seenCompletionIds = new Set<string>();
  private completionNotificationBaselineReady = false;

  // Config modal state
  showConfigModal: boolean = false;
  configEnabledTypes: Set<NotificationType> = new Set();
  allNotificationTypes: readonly NotificationType[] = ALL_NOTIFICATION_TYPES;
  loadingConfig: boolean = false;
  savingConfig: boolean = false;
  expandedNotifications: Set<number> = new Set();

  showCompanyMenu = false;
  showLangMenu = false;
  searchOpen = false;
  isTeamInbox = false;
  setupCompleted: Record<string, boolean> = {};
  companyActivity: string | undefined;
  myAvatarUrl: string | undefined;
  private routerSubscription?: Subscription;

  readonly languages = [
    { code: "fr", label: "Français" },
    { code: "en", label: "English" },
    { code: "ar", label: "العربية" },
  ];

  get toolbarMenuOpen(): boolean {
    return this.showCompanyMenu || this.showLangMenu || this.showNotifications;
  }

  private objectUrls: string[] = [];

  constructor(
    private translate: TranslateService,
    private companyService: CompanyApiService,
    private sharedLogoHeaderService: SharedLogoHeaderService,
    private authService: AuthService,
    private metaNotificationService: MetaNotificationApiService,
    private metaNotificationConfigService: MetaNotificationConfigApiService,
    private loaderService: LoaderService,
    private router: Router,
    private cdr: ChangeDetectorRef,
    private pendingMessagesService: PendingMessagesService,
    private sanitizer: DomSanitizer,
    private contactApiService: ContactApiService,
    private dialog: MatDialog,
    private keycloakService: KeycloakService,
    private alertService: AlertService,
    private onboardingTracker: OnboardingTrackerService,
    private myAvatarApiService: MyAvatarApiService,
    private companyTime: CompanyTimeService,
    readonly sidebarPanel: SidebarPanelService,
  ) {}

  /** Phones: search bar shown under the header (the inline bar does not fit). */
  mobileSearchOpen = false;

  toggleMobileNavigation(): void {
    this.closeToolbarMenus();
    this.closeNotifications();
    this.mobileSearchOpen = false;
    this.searchOpen = false;
    this.signalHeaderMenuOpened();
    this.sidebarPanel.toggleMobile();
  }

  toggleMobileSearch(): void {
    this.mobileSearchOpen = !this.mobileSearchOpen;
    if (this.mobileSearchOpen) {
      setTimeout(() => (document.querySelector(".header-search-slot--mobile input") as HTMLInputElement | null)?.focus());
    }
  }

  toggleExpand(event: Event, notificationId: number | undefined) {
    if (notificationId === undefined) return;
    event.stopPropagation();
    if (this.expandedNotifications.has(notificationId)) {
      this.expandedNotifications.delete(notificationId);
    } else {
      this.expandedNotifications.add(notificationId);
    }
    this.cdr.detectChanges();
  }

  isExpanded(notificationId: number | undefined): boolean {
    return (
      notificationId !== undefined &&
      this.expandedNotifications.has(notificationId)
    );
  }

  getTemplateName(description: string | undefined): string {
    if (!description) return "";

    const match = description.match(/Nom:\s*(.*?)(?:\n\s*\n|\n\[|$)/s);
    return match?.[1]?.trim() || "";
  }
  private extractTemplateJson(description: string | undefined): any[] {
    if (!description) return [];

    const start = description.indexOf("[");
    const end = description.lastIndexOf("]");

    if (start === -1 || end === -1 || end < start) {
      return [];
    }

    const jsonStr = description.substring(start, end + 1).trim();

    try {
      return JSON.parse(jsonStr);
    } catch {
      return [];
    }
  }

  getTemplateHeaderType(description: string | undefined): string {
    const content = this.extractTemplateJson(description);
    const header = content.find((item: any) => item.type === "HEADER");
    return header?.format || "";
  }

  getTemplateButtons(description: string | undefined): any[] {
    const content = this.extractTemplateJson(description);
    const buttons = content.find((item: any) => item.type === "BUTTONS");
    return Array.isArray(buttons?.buttons) ? buttons.buttons : [];
  }

  getCleanTemplateContent(description: string | undefined): string {
    const content = this.extractTemplateJson(description);
    const bodyItem = content.find((item: any) => item.type === "BODY");

    if (!bodyItem?.text) {
      return "";
    }

    let bodyText = bodyItem.text;

    if (
      bodyItem.example &&
      Array.isArray(bodyItem.example.body_text) &&
      Array.isArray(bodyItem.example.body_text[0])
    ) {
      bodyItem.example.body_text[0].forEach((val: string, index: number) => {
        bodyText = bodyText.split(`{{${index + 1}}}`).join(`<b>${val}</b>`);
      });
    }

    return bodyText.trim();
  }

  getFormattedDescription(
    description: string | undefined,
    notificationType?: string,
  ): string {
    if (!description) return "";

    const trimmed = description.trim();

    if (this.isMediaNotificationType(notificationType)) {
      return "";
    }

    if (this.isMediaPlaceholderDescription(trimmed)) {
      return "";
    }

    if (
      notificationType === "CONTACT_UPLOAD_BATCH_FINISHED" ||
      notificationType === "CONTACT_UPLOAD"
    ) {
      return this.translate.instant(
        "NOTIFICATION.CONTACT_UPLOAD_BATCH.FINISHED",
      );
    }

    if (notificationType === "IMPORT_JOB_FINISHED") {
      return this.importDescription(trimmed);
    }

    if (notificationType === "PAYMENT_CAPTURE_REQUIRED") {
      return this.formatPaymentCaptureDescription(trimmed);
    }

    if (this.isContactUploadReason(trimmed)) {
      return translateContactUploadReason(trimmed, this.translate);
    }
    const decodedReplyCode = decodeInteractiveReplyCode(trimmed, (key) =>
      this.translate.instant(key),
    );
    if (decodedReplyCode) {
      return decodedReplyCode;
    }
    if (trimmed.startsWith("{") && trimmed.endsWith("}")) {
      try {
        const obj = JSON.parse(trimmed);
        const lines: string[] = [];
        for (const key of Object.keys(obj)) {
          if (key === "flow_token") continue;
          const val = obj[key];
          if (Array.isArray(val)) {
            const filesInfo = val
              .map((item: any) => {
                if (item && typeof item === "object") {
                  const fileName = item.file_name || item.name || "";
                  const mimeType = item.mime_type || item.mimeType || "";
                  if (mimeType.startsWith("image/")) {
                    return `[image] ${fileName}`;
                  } else {
                    return `[file] ${fileName}`;
                  }
                }
                return String(item);
              })
              .join(", ");
            lines.push(filesInfo);
          } else if (val && typeof val === "object") {
            lines.push(JSON.stringify(val));
          } else {
            lines.push(String(val));
          }
        }
        if (lines.length > 0) {
          const prefix =
            this.translate.instant("FLOW_META.MEDIA.FLOW_RESPONSE") ||
            "Flow response:";
          return prefix.replace(/\s*:\s*$/, "");
        }
      } catch (e) {
        // Fall back
      }
    }
    return description;
  }

  private formatPaymentCaptureDescription(description: string): string {
    const fields = new Map<string, string>();
    description.split(/\r?\n/).forEach((line) => {
      const separator = line.indexOf(":");
      if (separator <= 0) return;
      fields.set(
        line.slice(0, separator).trim().toLowerCase(),
        line.slice(separator + 1).trim(),
      );
    });

    const translatedLines: string[] = [];
    const addField = (sourceLabel: string, translationKey: string) => {
      const value = fields.get(sourceLabel);
      if (value) {
        translatedLines.push(
          `[${this.translate.instant(translationKey)}] ${value}`,
        );
      }
    };

    addField("transaction", "NOTIFICATION.PAYMENT_CAPTURE.TRANSACTION");
    addField("authorization", "NOTIFICATION.PAYMENT_CAPTURE.AUTHORIZATION");
    addField(
      "remaining amount",
      "NOTIFICATION.PAYMENT_CAPTURE.REMAINING_AMOUNT",
    );

    const reminder = fields.get("reminder threshold");
    const days = reminder?.match(/\d+/)?.[0];
    if (days) {
      translatedLines.push(
        `[${this.translate.instant("NOTIFICATION.PAYMENT_CAPTURE.REMINDER")}] ${this.translate.instant("NOTIFICATION.PAYMENT_CAPTURE.DAYS", { days })}`,
      );
    }

    return translatedLines.length > 0
      ? translatedLines.join("\n")
      : this.translate.instant("NOTIFICATION.PAGE.ACTION_REQUIRED_HINT");
  }

  private openContactUploadBatchModal(batchReference: string): void {
    this.loaderService.show();
    this.contactApiService
      .getUploadBatchStatus({
        batchReference,
        "x-api-requestId": uuidv4(),
        "x-api-canal": "web",
      })
      .subscribe({
        next: (response) => {
          this.loaderService.hide();
          const batchData = response.body;
          if (batchData) {
            ensureTranslateLangLoaded(this.translate).subscribe(() => {
              this.dialog.open(ContactUploadBatchModalComponent, {
                data: { batchData },
                width: "640px",
                minHeight: "300px",
                maxHeight: "85vh",
                hasBackdrop: true,
                panelClass: "contact-upload-batch-modal",
              });
            });
          }
        },
        error: () => {
          this.loaderService.hide();
        },
      });
  }

  private isContactUploadReason(value: string): boolean {
    const normalized = value.trim().toUpperCase();
    const knownTypes = [
      "INVALID_PHONE",
      "DUPLICATE_CONTACT",
      "PHONE_FORMAT",
      "FIXED_NUMBER",
      "PHONE_DUPLICATED",
      "MISSING_FIELDS",
      "MISSING_PHONE",
      "INVALID_MOBILE",
      "MUST_START_WITH_PLUS",
      "PHONE_TOO_SHORT",
      "PHONE_TOO_LONG",
      "INVALID_FORMAT",
    ];
    return knownTypes.includes(normalized) || value.startsWith("CONTACT.");
  }

  ngOnInit() {
    initFlowbite();
    this.currentLanguage = this.translate.currentLang;
    this.loadSetupProgress();
    if (this.shouldLoadCompanyBranding()) {
      this.loadCompanyProfile();
      this.getCompanyLogo();
    } else {
      this.companyTime.setCompanyZone();
    }
    if (this.shouldLoadCompanyActivity()) {
      this.loadCompanyActivity();
    }
    if (this.authService.getCompanyId()) {
      this.loadMyAvatar();
    }
    this.logoSubscription = this.sharedLogoHeaderService.logo$.subscribe(
      (logo) => {
        if (logo) {
          this.setCompanyLogoUrl(logo);
        }
      },
    );
    this.initializeNotifications();
    this.syncTeamInboxFlag(this.router.url);
    this.trackSetupFromRoute(this.router.url);
    this.routerSubscription = this.router.events
      .pipe(
        filter(
          (event): event is NavigationEnd => event instanceof NavigationEnd,
        ),
      )
      .subscribe((event) => {
        const url = event.urlAfterRedirects || event.url;
        this.syncTeamInboxFlag(url);
        this.trackSetupFromRoute(url);
      });
  }

  private syncTeamInboxFlag(url: string): void {
    this.isTeamInbox = url.split("?")[0] === APP_PATHS.messaging.inbox;
  }

  // ---- Header toolbar ----

  isUserInRole(roles: string[] | undefined): boolean {
    if (!roles || roles.length === 0) return true;
    if (roles.includes(Role.ADMIN_SOLUTION_UI)) {
      return roles.every((role) => this.keycloakService.isUserInRole(role));
    }
    return roles.some((role) => this.keycloakService.isUserInRole(role));
  }

  hasUserGroup(requiredGroups: string[] | undefined): boolean {
    return this.authService.hasAnyGroup(requiredGroups ?? []);
  }

  private isUserInRoleForLink(item: HeaderToolbarLink): boolean {
    const roles = item.authorities;
    if (!roles || roles.length === 0) {
      return true;
    }
    if (item.roleMode === "all") {
      return roles.every((role) => this.keycloakService.isUserInRole(role));
    }
    return this.isUserInRole(roles);
  }

  private isToolbarLinkVisible(item: HeaderToolbarLink): boolean {
    if (item.excludeIfRole && this.isUserInRole([item.excludeIfRole])) {
      return false;
    }
    const allowed = item.authorities?.length
      ? this.isUserInRoleForLink(item)
      : this.hasUserGroup(item.groups);
    return allowed && this.isToolbarLinkVisibleForActivity(item);
  }

  private shouldLoadCompanyBranding(): boolean {
    // WABA exposes read-only company branding to every authenticated tenant user.
    return !!this.authService.getCompanyId();
  }

  private shouldLoadCompanyActivity(): boolean {
    return this.authService.hasAnyGroup(["/ORDER"]);
  }

  private isMediaNotificationType(type?: string): boolean {
    return ["IMAGE", "VIDEO", "AUDIO", "DOCUMENT"].includes(type || "");
  }

  getNotificationSender(notification: {
    type?: string;
    title?: string;
  }): string {
    const messageTypes = [
      "MESSAGE",
      "TEXT",
      "IMAGE",
      "VIDEO",
      "AUDIO",
      "DOCUMENT",
      "LOCATION",
      "CONTACTS",
      "ORDER",
      "REACTION",
    ];
    if (!messageTypes.includes(notification.type || "")) {
      return "";
    }

    return (notification.title || "")
      .replace(/^message\s+(de|from)\s+/i, "")
      .trim();
  }

  private isMediaPlaceholderDescription(description: string): boolean {
    const normalized = description.trim().toLowerCase();
    return (
      /^\[(media|image|video|audio|document|file)\](\s|$)/i.test(normalized) ||
      /^\[(media|image|video|audio|document|file)\]$/i.test(normalized)
    );
  }

  get showNotificationBell(): boolean {
    return (
      this.canUsePaymentNotifications() ||
      (this.canUseMessagingNotifications() &&
        (this.hasWhatsAppAccount || this.isUserInRole([Role.MANAGE_CONTACT])))
    );
  }

  /** Notification APIs + polling: /MESSAGING group and a messaging-related role only. */
  private canUseMessagingNotifications(): boolean {
    return this.isUserInRole([...MESSAGING_NOTIFICATION_ROLES]);
  }

  private canUsePaymentNotifications(): boolean {
    return (
      this.authService.hasAnyGroup(["/PAYMENT"]) &&
      this.isUserInRole([...PAYMENT_NOTIFICATION_ROLES])
    );
  }

  get visibleNotificationTypes(): readonly NotificationType[] {
    if (
      this.canUsePaymentNotifications() &&
      !this.canUseMessagingNotifications()
    ) {
      return ["PAYMENT_CAPTURE_REQUIRED"];
    }
    return this.allNotificationTypes.filter(
      (type) =>
        !KPI_RECAP_NOTIFICATION_TYPES.includes(type) &&
        (type !== "PAYMENT_CAPTURE_REQUIRED" ||
          this.canUsePaymentNotifications()),
    );
  }

  get visibleKpiRecapNotificationTypes(): readonly NotificationType[] {
    return KPI_RECAP_NOTIFICATION_TYPES;
  }

  private canUseNotifications(): boolean {
    return (
      this.canUseMessagingNotifications() || this.canUsePaymentNotifications()
    );
  }

  get showGlobalSearch(): boolean {
    const userGroups = this.authService.getUserGroups() || [];
    const groupAccess = userGroups.some((g) =>
      [
        "/MESSAGING",
        "/SUPPORT",
        "/ADMINISTRATION",
        "/SOLUTION",
        "/ORDER",
        "/PAYMENT",
      ].some((required) => g === required || g.startsWith(required)),
    );
    return (
      groupAccess ||
      this.isUserInRole([
        Role.CONTACT_VIEW,
        Role.MANAGE_CONTACT,
        Role.ORDERS_VIEW,
        Role.ORDERS,
        Role.TICKET_VIEW,
        Role.MANAGE_TICKET,
        Role.CONVERSATION_VIEW,
        Role.TEAM_INBOX,
        Role.PRODUCT_VIEW,
        Role.PRODUCT,
        Role.PAYMENT_VIEW,
        Role.BROADCAST_VIEW,
        Role.BROADCAST_HISTORY,
      ])
    );
  }

  shouldShowNotificationBody(notification: {
    type?: string;
    mediaItems?: unknown[];
    description?: string;
  }): boolean {
    const type = notification.type;
    if (this.isMediaNotificationType(type)) {
      return false;
    }
    return !!this.getFormattedDescription(notification.description, type);
  }

  getNotificationDescriptionParts(
    description: string | undefined,
    notificationType?: string,
  ): { text: string; label: boolean }[] {
    const formatted = this.getFormattedDescription(
      description,
      notificationType,
    );
    if (!formatted) {
      return [];
    }

    const parts: { text: string; label: boolean }[] = [];
    const labelPattern = /\[[^\]]+\]/g;
    let lastIndex = 0;
    let match: RegExpExecArray | null;

    while ((match = labelPattern.exec(formatted)) !== null) {
      if (match.index > lastIndex) {
        parts.push({
          text: formatted.slice(lastIndex, match.index),
          label: false,
        });
      }
      parts.push({ text: match[0].slice(1, -1), label: true });
      lastIndex = match.index + match[0].length;
    }

    if (lastIndex < formatted.length) {
      parts.push({ text: formatted.slice(lastIndex), label: false });
    }

    return parts.length > 0 ? parts : [{ text: formatted, label: false }];
  }

  private loadCompanyActivity(): void {
    const companyId = this.authService.getCompanyId();
    if (!companyId) {
      return;
    }

    this.companyService
      .getActivity(
        {
          "x-api-requestId": uuidv4(),
          "x-api-canal": "web",
          companyId,
        },
        SILENT_HTTP_CONTEXT,
      )
      .subscribe({
        next: (response) => {
          this.companyActivity = response.body;
        },
        error: () => {
          this.companyActivity = undefined;
        },
      });
  }

  private loadMyAvatar(): void {
    this.myAvatarApiService.getMyAvatarUrl().subscribe({
      next: (url) => {
        this.myAvatarUrl = url;
        this.cdr.detectChanges();
      },
      error: () => {
        this.myAvatarUrl = undefined;
      },
    });
  }

  private isToolbarLinkVisibleForActivity(item: HeaderToolbarLink): boolean {
    if (item.hideForActivity && this.companyActivity === item.hideForActivity) {
      return false;
    }
    if (
      item.activityCondition &&
      this.companyActivity !== item.activityCondition
    ) {
      return false;
    }
    return true;
  }

  get visibleCompanyMenu(): HeaderToolbarLink[] {
    return HEADER_COMPANY_MENU.filter((item) =>
      this.isToolbarLinkVisible(item),
    );
  }

  private isSetupStepVisible(step: HeaderSetupStep): boolean {
    if (step.excludeIfRole && this.isUserInRole([step.excludeIfRole])) {
      return false;
    }
    const roles = step.authorities;
    if (roles?.length) {
      const allowed =
        step.roleMode === "all"
          ? roles.every((role) => this.keycloakService.isUserInRole(role))
          : this.isUserInRole(roles);
      if (!allowed) {
        return false;
      }
    }
    return roles?.length ? true : this.hasUserGroup(step.groups);
  }

  get visibleSetupSteps(): HeaderSetupStep[] {
    return HEADER_SETUP_STEPS.filter((step) => this.isSetupStepVisible(step));
  }

  get visibleOptionalSteps(): HeaderSetupStep[] {
    return HEADER_OPTIONAL_STEPS.filter(
      (step) =>
        (step.authorities?.length
          ? this.isUserInRole(step.authorities)
          : this.hasUserGroup(step.groups)) &&
        !(
          step.hideForActivity && this.companyActivity === step.hideForActivity
        ),
    );
  }

  toggleCompanyMenu(event: Event) {
    event.stopPropagation();
    this.signalHeaderMenuOpened();
    this.showCompanyMenu = !this.showCompanyMenu;
    this.showLangMenu = false;
    this.showNotifications = false;
  }

  toggleLangMenu(event: Event) {
    event.stopPropagation();
    this.signalHeaderMenuOpened();
    this.showLangMenu = !this.showLangMenu;
    this.showCompanyMenu = false;
    this.showNotifications = false;
  }

  closeToolbarMenus() {
    this.showCompanyMenu = false;
    this.showLangMenu = false;
  }

  private signalHeaderMenuOpened(): void {
    window.dispatchEvent(new CustomEvent("dashy-header-menu-opened"));
  }

  navigateFromToolbar(path: string) {
    const isAllowed =
      this.visibleSetupSteps.some((step) =>
        this.toolbarPathsMatch(path, step.path),
      ) ||
      [...HEADER_COMPANY_MENU, ...HEADER_QUICK_APPS].some(
        (item) =>
          this.isToolbarLinkVisible(item) &&
          this.toolbarPathsMatch(path, item.path),
      );
    if (!isAllowed) {
      return;
    }
    this.closeToolbarMenus();
    this.router.navigateByUrl(path);
  }

  private toolbarPathsMatch(target: string, allowed: string): boolean {
    if (target === allowed) {
      return true;
    }
    const targetBase = target.split("?")[0];
    const allowedBase = allowed.split("?")[0];
    return targetBase === allowedBase;
  }

  openHelp() {
    const platformPath = APP_PATHS.platform.companies;
    const ticketsPath = APP_PATHS.messaging.tickets;
    if (
      this.isUserInRole([
        Role.ADMIN_CONVERSATIONS,
        Role.ADMIN_SOLUTION_UI,
        Role.DASHY_COMPANIES,
      ])
    ) {
      this.router.navigateByUrl(platformPath);
      return;
    }
    if (this.isUserInRole([Role.MANAGE_TICKET])) {
      this.router.navigateByUrl(ticketsPath);
      return;
    }
    this.router.navigateByUrl(platformPath);
  }

  openSettings() {
    const target =
      this.visibleCompanyMenu.find((item) => item.id === "company-settings") ??
      this.visibleCompanyMenu[0];
    if (target) {
      this.navigateFromToolbar(target.path);
    }
  }

  private loadSetupProgress() {
    try {
      const raw = localStorage.getItem(SETUP_STORAGE_KEY);
      this.setupCompleted = raw ? JSON.parse(raw) : {};
    } catch {
      this.setupCompleted = {};
    }
  }

  private saveSetupProgress() {
    try {
      localStorage.setItem(
        SETUP_STORAGE_KEY,
        JSON.stringify(this.setupCompleted),
      );
    } catch {
      // Ignore storage errors
    }
  }

  /** Ids tracked from real account data instead of "the user visited the page". */
  private static readonly DATA_DRIVEN_STEP_IDS = new Set([
    "meta",
    "templates",
    "messaging",
  ]);

  private trackSetupFromRoute(url: string) {
    const path = (url || "").replace(/^#/, "");
    let changed = false;
    for (const step of [
      ...this.visibleSetupSteps,
      ...this.visibleOptionalSteps,
    ]) {
      if (HeaderComponent.DATA_DRIVEN_STEP_IDS.has(step.id)) {
        continue;
      }
      if (!this.setupCompleted[step.id] && path.startsWith(step.pathPrefix)) {
        this.setupCompleted[step.id] = true;
        this.onboardingTracker.setupStepCompleted(step.id);
        changed = true;
      }
    }
    if (changed) {
      this.saveSetupProgress();
      this.cdr.detectChanges();
    }
  }

  private initializeNotifications(): void {
    if (!this.canUseNotifications()) {
      this.hasWhatsAppAccount = false;
      this.showNotifications = false;
      return;
    }

    // Contact-import completion notifications are valid even when the tenant has no
    // WhatsApp number yet, so eligibility is role-based rather than account-based.
    this.refreshHasWhatsAppAccount();
    this.getNotifications();
    this.startPolling();
  }

  /**
   * Lightweight refresh of hasWhatsAppAccount only — used by the setup
   * checklist to re-check completion without re-arming notification polling
   * (initializeNotifications() does that and must only run once).
   */
  private refreshHasWhatsAppAccount(
    onLoaded?: (hasAccount: boolean) => void,
  ): void {
    this.companyService
      .companyNumbersList(
        {
          "x-api-canal": "web",
          "x-api-requestId": uuidv4(),
        },
        SILENT_HTTP_CONTEXT,
      )
      .subscribe({
        next: (response) => {
          this.hasWhatsAppAccount = (response.body?.length ?? 0) > 0;
          if (this.hasWhatsAppAccount) {
            this.onboardingTracker.setupStepCompleted(
              "meta",
              "WhatsApp number connected",
            );
          }
          onLoaded?.(this.hasWhatsAppAccount);
        },
        error: () => {
          this.hasWhatsAppAccount = false;
          // Payment and contact-import notifications do not require WhatsApp.
          if (!this.showNotificationBell) this.showNotifications = false;
        },
      });
  }

  getCurrentLang() {
    return sessionStorage.getItem("lang") || this.translate.currentLang || "fr";
  }

  get isRTL() {
    return this.getCurrentLang() === "ar";
  }

  get companyInitial(): string {
    const source = (
      this.companyName ||
      this.authService.getCompanyId() ||
      ""
    ).trim();
    if (!source) {
      return "?";
    }
    return source.charAt(0).toUpperCase();
  }

  get showCompanyMenuDropdown(): boolean {
    return this.visibleCompanyMenu.length > 0;
  }

  /** Company logo in the brand rail — home only, never deep-link to settings. */
  get companyBrandLink(): string {
    return "/";
  }

  private loadCompanyProfile() {
    this.companyTime.beginResolution();
    const companyId = this.authService.getCompanyId();
    if (!companyId) {
      this.companyTime.setCompanyZone();
      return;
    }

    this.companyService
      .getCompanyByCompanyId(
        {
          "x-api-canal": "web",
          "x-api-requestId": uuidv4(),
        },
        SILENT_HTTP_CONTEXT,
      )
      .subscribe({
        next: (response) => {
          const company = response?.body;
          if (company?.name) {
            this.companyName = company.name;
          }
          if (company?.brandColor) {
            this.companyBrandColor = company.brandColor;
          }
          this.companyTime.setCompanyZone(
            company?.timeZoneId,
            company?.timezone,
            company?.country,
          );
        },
        error: () => this.companyTime.setCompanyZone(),
      });
  }

  private getCompanyLogo() {
    this.companyService
      .getCompanyLogo(undefined, SILENT_HTTP_CONTEXT)
      .subscribe({
        next: (response: any) => {
          if (response?.size > 0) {
            this.setCompanyLogoUrl(URL.createObjectURL(response));
          }
        },
        error: () => {},
      });
  }

  private setCompanyLogoUrl(url: string) {
    if (this.ownedLogoUrl && this.ownedLogoUrl !== url) {
      URL.revokeObjectURL(this.ownedLogoUrl);
    }
    if (url.startsWith("blob:")) {
      this.ownedLogoUrl = url;
    }
    this.companyLogoUrl = url;
    this.cdr.detectChanges();
  }

  get currentLangLabel(): string {
    const code = this.getCurrentLang();
    return this.getLanguageLabel(code);
  }

  getLanguageLabel(code: string): string {
    const labels: Record<string, string> = {
      fr: "Français",
      en: "English",
      ar: "العربية",
    };
    return labels[code] || code;
  }

  selectLanguage(language: string) {
    this.closeToolbarMenus();
    if (language === this.getCurrentLang()) return;
    sessionStorage.setItem("lang", language);
    void this.translate.use(language);
  }

  toggleNotifications() {
    if (!this.showNotificationBell) return;
    this.signalHeaderMenuOpened();
    this.showNotifications = !this.showNotifications;
    if (this.showNotifications) {
      this.closeToolbarMenus();
      this.updateNotificationDropdownPosition();
    }
  }

  closeNotifications(restoreFocus = false): void {
    this.showNotifications = false;
    if (restoreFocus) {
      this.notificationToggle?.nativeElement.focus();
    }
  }

  private updateNotificationDropdownPosition() {
    const toggle = this.notificationToggle?.nativeElement;
    if (!toggle) return;

    const rect = toggle.getBoundingClientRect();
    const margin = 12;
    // Never wider than the screen (phones): full width minus the side margins.
    const width = Math.min(380, window.innerWidth - margin * 2);
    const left = this.isRTL
      ? Math.min(rect.left, window.innerWidth - width - margin)
      : Math.max(margin, rect.right - width);

    this.notificationDropdownStyle = {
      top: `${rect.bottom + 8}px`,
      left: `${Math.max(margin, left)}px`,
      width: `${width}px`,
    };
  }

  getNotificationTime(
    canonicalUtc: string | undefined,
    legacyDate?: string,
  ): string {
    const d = this.companyTime.parseCanonical(canonicalUtc);
    if (!d) return this.companyTime.format(undefined, legacyDate);

    const now = new Date();
    const diffMin = Math.floor((now.getTime() - d.getTime()) / 60000);

    if (diffMin < 1) return this.translate.instant("NOTIFICATION.TIME.NOW");
    if (diffMin < 60)
      return this.translate.instant("NOTIFICATION.TIME.MIN", { n: diffMin });
    if (diffMin < 24 * 60) {
      return this.translate.instant("NOTIFICATION.TIME.HOUR", {
        n: Math.floor(diffMin / 60),
      });
    }
    return this.companyTime.format(canonicalUtc);
  }

  getNotificationTitle(notification: { type?: string; title?: string; referenceType?: string }): string {
    if (notification.type === "IMPORT_JOB_FINISHED") {
      const title = this.importTitle(notification);
      if (title) return title;
    }
    return notification.type
      ? this.translate.instant("NOTIFICATION.TYPE." + notification.type)
      : notification.title || "";
  }

  /** "Salon novembre : import terminé", from the import's own name. */
  private importTitle(notification: { title?: string; description?: string; referenceType?: string }): string {
    const notice = importNotice(notification.title, notification.description);
    if (!notice) return "";
    // an import the merchant did not name is named by what it brought in ("Produits : import terminé")
    const entity = (notification.referenceType || "").replace("IMPORT_JOB:", "");
    const name =
      DEFAULT_IMPORT_NAMES.includes(notice.name) && entity
        ? this.translate.instant("IMPORT.TYPE." + entity)
        : notice.name;
    return this.translate.instant("NOTIFICATION.ALERT.IMPORT_JOB." + notice.status, { name });
  }

  /** "1 ajouté · 300 mis à jour · 3 à corriger", in the merchant's language; a count of zero is left out. */
  private importDescription(description: string): string {
    const counts = importCounts(description);
    if (counts.archived) {
      return this.translate.instant("NOTIFICATION.ALERT.IMPORT_JOB.ARCHIVED");
    }
    if (counts.created === undefined) {
      return this.translate.instant("NOTIFICATION.ALERT.IMPORT_JOB.STOPPED");
    }
    const parts: string[] = [];
    for (const [key, count] of [
      ["ADDED", counts.created],
      ["UPDATED", counts.updated],
      ["SKIPPED", counts.skipped],
    ] as const) {
      if (count) {
        parts.push(this.translate.instant("NOTIFICATION.ALERT.IMPORT_JOB." + key, { count }));
      }
    }
    return parts.join(" · ");
  }

  getNotificationTone(
    type: string | undefined,
    notification?: { title?: string; description?: string },
  ): string {
    if (type === "IMPORT_JOB_FINISHED" && notification) {
      // orange when rows were left out, red when nothing was saved
      const status = importNotice(notification.title, notification.description)?.status;
      if (status === "PARTIAL") return "warning";
      if (status === "FAILED") return "danger";
    }
    switch (type) {
      case "TEMPLATE_APPROVED":
      case "CONTACT_UPLOAD_BATCH_FINISHED":
      case "IMPORT_JOB_FINISHED":
      case "PENDING_MESSAGE_EXPORT_FINISHED":
        return "success";
      case "TEMPLATE_REJECTED":
      case "PAYMENT_CAPTURE_REQUIRED":
        return "danger";
      case "ORDER":
        return "warning";
      case "IMAGE":
      case "VIDEO":
      case "AUDIO":
      case "DOCUMENT":
      case "LOCATION":
      case "CONTACTS":
        return "info";
      default:
        return "accent";
    }
  }

  private notifyCompletedTasks(entities: any[]): void {
    const completionNotifications = entities.filter(
      (notification: any) =>
        notification.type === "CONTACT_UPLOAD_BATCH_FINISHED" ||
        notification.type === "IMPORT_JOB_FINISHED" ||
        notification.type === "BROADCAST_EXPORT_FINISHED" ||
        notification.type === "PENDING_MESSAGE_EXPORT_FINISHED",
    );

    // The initial response represents the notification inbox, not new events.
    // Keep it in the bell, but do not replay a toast after a browser refresh.
    if (!this.completionNotificationBaselineReady) {
      completionNotifications.forEach((notification: any) => {
        const id = String(
          notification.reference ||
            notification.id ||
            notification.referenceId ||
            "",
        );
        if (id) this.seenCompletionIds.add(id);
      });
      this.completionNotificationBaselineReady = true;
      return;
    }

    completionNotifications.forEach((notification: any) => {
      const alertId = String(
        notification.reference ||
          notification.id ||
          notification.referenceId ||
          "",
      );
      if (
        notification.readStatus ||
        !alertId ||
        this.seenCompletionIds.has(alertId)
      ) {
        return;
      }

      this.seenCompletionIds.add(alertId);
      this.alertService.showResult(this.completionResult(notification));
    });
  }

  /** The popup for a finished import or export: what it was, what happened, and a link to open it. */
  private completionResult(notification: any): AlertResult {
    const open = () => this.markAsRead(notification);
    if (notification.type === "IMPORT_JOB_FINISHED") {
      const status = importNotice(notification.title, notification.description)?.status;
      return {
        tone: status === "FAILED" ? "error" : status === "PARTIAL" ? "warning" : "success",
        title:
          this.importTitle(notification) ||
          this.translate.instant("NOTIFICATION.ALERT.IMPORT_COMPLETED"),
        detail: this.importDescription((notification.description || "").trim()),
        actionLabel: this.translate.instant("NOTIFICATION.ALERT.OPEN_IMPORT"),
        action: open,
      };
    }
    const messages: Record<string, string> = {
      CONTACT_UPLOAD_BATCH_FINISHED: "NOTIFICATION.ALERT.CONTACT_IMPORT_COMPLETED",
      BROADCAST_EXPORT_FINISHED: "NOTIFICATION.ALERT.BROADCAST_EXPORT_COMPLETED",
      PENDING_MESSAGE_EXPORT_FINISHED: "NOTIFICATION.ALERT.PENDING_MESSAGE_EXPORT_COMPLETED",
    };
    const isExport = notification.type !== "CONTACT_UPLOAD_BATCH_FINISHED";
    return {
      tone: "success",
      title: this.translate.instant(messages[notification.type]),
      actionLabel: this.translate.instant(
        isExport ? "NOTIFICATION.ALERT.DOWNLOAD_EXPORT" : "NOTIFICATION.ALERT.OPEN_IMPORT",
      ),
      action: open,
    };
  }

  processNotifications(entities: any[]) {
    entities.forEach((notification: any) => {
      if (notification.mediaItems) return; // Already processed

      const desc = notification.description;
      if (desc && desc.trim().startsWith("{") && desc.trim().endsWith("}")) {
        try {
          const obj = JSON.parse(desc.trim());
          const mediaItems: any[] = [];

          for (const key of Object.keys(obj)) {
            if (key === "flow_token") continue;
            const val = obj[key];
            if (Array.isArray(val)) {
              val.forEach((item: any) => {
                if (
                  item &&
                  typeof item === "object" &&
                  (item.id || item.mediaId)
                ) {
                  const mediaId = item.id || item.mediaId;
                  const mimeType = item.mime_type || item.mimeType || "";
                  const fileName = item.file_name || item.name || "";

                  const mediaObj = {
                    mediaId: String(mediaId),
                    mimeType: mimeType,
                    fileName: fileName,
                    safeUrl: null as SafeUrl | null,
                    loading: true,
                    error: false,
                  };
                  mediaItems.push(mediaObj);

                  // Fetch media data
                  this.pendingMessagesService
                    .getMediaByMediaId({
                      "x-api-requestId": uuidv4(),
                      "x-api-canal": "web",
                      mediaId: String(mediaId),
                    })
                    .subscribe({
                      next: (blob) => {
                        const objectUrl = URL.createObjectURL(blob);
                        this.objectUrls.push(objectUrl);
                        mediaObj.safeUrl =
                          this.sanitizer.bypassSecurityTrustUrl(objectUrl);
                        mediaObj.loading = false;
                        this.cdr.detectChanges();
                      },
                      error: () => {
                        mediaObj.error = true;
                        mediaObj.loading = false;
                        this.cdr.detectChanges();
                      },
                    });
                }
              });
            }
          }
          if (mediaItems.length > 0) {
            notification.mediaItems = mediaItems;
          }
        } catch (e) {
          // ignore
        }
      }
    });
  }

  getNotifications(append: boolean = false) {
    if (!this.canUseNotifications()) return;
    if (this.loadingNotifications) return;
    this.loadingNotifications = true;

    this.metaNotificationService
      .getNotifications({
        "x-api-requestId": uuidv4(),
        "x-api-canal": "web",
        page: this.page,
        size: this.size,
        unreadOnly: this.unreadOnly,
      })
      .subscribe({
        next: (response) => {
          if (response.body && response.body.notifications) {
            const entities = this.sortNotifications(
              response.body.notifications.entities || [],
            );
            this.notifyCompletedTasks(entities);
            if (append) {
              const merged = [...this.notifications, ...entities];
              this.processNotifications(merged);
              this.notifications = merged;
            } else {
              this.processNotifications(entities);
              this.notifications = entities;
            }
            this.totalElements = response.body.notifications.totalElements || 0;
            this.unreadCount = response.body.unreadCount || 0;
            this.actionRequiredCount = response.body.actionRequiredCount || 0;
            this.paymentAction = response.body.paymentAction;
            this.cdr.detectChanges();
          }
          this.loadingNotifications = false;
          this.cdr.detectChanges();
        },
        error: (error) => {
          this.loadingNotifications = false;
          this.cdr.detectChanges();
        },
      });
  }

  loadMoreNotifications(event: Event) {
    event.stopPropagation();
    this.page++;
    this.getNotifications(true);
  }

  /** After a catalog import, the list of what was imported. */
  markAsRead(notification: any) {
    const isExportDownload =
      !!notification.referenceId &&
      (notification.referenceType === "BROADCAST_EXPORT" ||
        notification.referenceType === "PENDING_MESSAGE_EXPORT");

    const navigate = () => {
      this.showNotifications = false;
      const ref = notification.referenceId;
      const refType = notification.referenceType;
      const notifType = notification.type;

      if (ref && refType === "CONVERSATION") {
        this.router.navigate([APP_PATHS.messaging.inbox], {
          queryParams: { conversationRef: ref },
        });
      } else if (ref && refType === "TEMPLATE") {
        this.router.navigateByUrl(APP_PATHS.workflow.templateRequest(ref));
      } else if (
        ref &&
        (refType === "CONTACT_UPLOAD_BATCH" ||
          notifType === "CONTACT_UPLOAD_BATCH_FINISHED")
      ) {
        this.openContactUploadBatchModal(ref);
      } else if (typeof refType === "string" && refType.startsWith("IMPORT_JOB:")) {
        // The import itself, in the history: what was added, and the rows that were not, with why.
        this.router.navigate([APP_PATHS.importHistory], {
          queryParams: { ref, type: refType.substring("IMPORT_JOB:".length) },
        });
      } else if (
        ref &&
        (refType === "BROADCAST_EXPORT" || refType === "PENDING_MESSAGE_EXPORT")
      ) {
        this.downloadExportWithoutNavigation(ref);
      } else if (ref && refType === "AUTHORIZATION") {
        // Notifications carry the S2M reference number, not the internal UUID
        // required by the detail API. Open the scoped list; its rows have that UUID.
        void this.router.navigate([APP_PATHS.payment.authorizations], {
          queryParams: { referenceNumber: ref },
        });
      }
    };

    // Open export URLs directly from the user's click. Waiting for the mark-as-read
    // request causes browsers to treat window.open as an unsolicited popup.
    if (isExportDownload) {
      navigate();
    }

    if (!notification.readStatus && notification.reference) {
      this.metaNotificationService
        .markAsRead({ reference: notification.reference })
        .subscribe({
          next: () => {
            this.applyLocalReadState(notification);
            this.cdr.detectChanges();
            if (!isExportDownload) navigate();
          },
          error: () => {
            if (!isExportDownload) navigate();
          },
        });
    } else {
      if (!isExportDownload) navigate();
    }
  }

  private downloadExportWithoutNavigation(url: string): void {
    const iframe = document.createElement("iframe");
    iframe.style.display = "none";
    iframe.setAttribute("aria-hidden", "true");
    iframe.src = url;
    document.body.appendChild(iframe);

    // Keep it long enough for the browser to start the download, then clean up.
    window.setTimeout(() => iframe.remove(), 60_000);
  }

  private applyLocalReadState(notification: any): void {
    if (notification.readStatus) return;
    notification.readStatus = true;
    this.unreadCount = Math.max(0, this.unreadCount - 1);
    if (notification.actionRequired && !notification.resolvedAt) {
      this.actionRequiredCount = Math.max(0, this.actionRequiredCount - 1);
    }
  }

  openPaymentActions(event: Event): void {
    event.stopPropagation();
    if (this.paymentAction?.referenceId) {
      this.markAsRead(this.paymentAction);
      return;
    }
    this.showNotifications = false;
    this.router.navigate([APP_PATHS.payment.authorizations], {
      queryParams: { status: "APPROVED" },
    });
  }

  private sortNotifications(notifications: any[]): any[] {
    return [...notifications].sort((left, right) => {
      const leftAction =
        left?.actionRequired && !left?.resolvedAt && !left?.readStatus ? 1 : 0;
      const rightAction =
        right?.actionRequired && !right?.resolvedAt && !right?.readStatus
          ? 1
          : 0;
      return rightAction - leftAction;
    });
  }

  toggleUnreadOnly() {
    this.unreadOnly = !this.unreadOnly;
    this.page = 0;
    this.notifications = [];
    this.getNotifications();
  }

  markAllAsRead() {
    if (this.markingAllAsRead) return;
    this.markingAllAsRead = true;
    this.cdr.detectChanges();

    this.metaNotificationService.markAllAsRead({}).subscribe({
      next: () => {
        this.notifications.forEach((n) => (n.readStatus = true));
        this.unreadCount = 0;
        this.actionRequiredCount = 0;
        this.markingAllAsRead = false;
        this.cdr.detectChanges();
        this.refreshNotifications();
      },
      error: () => {
        this.markingAllAsRead = false;
        this.cdr.detectChanges();
      },
    });
  }

  startPolling() {
    if (!this.canUseNotifications()) return;
    this.pollingSubscription = interval(10000).subscribe(() => {
      this.refreshNotifications();
    });
  }

  stopPolling() {
    if (this.pollingSubscription) {
      this.pollingSubscription.unsubscribe();
    }
  }

  openConfigModal() {
    this.showConfigModal = true;
    this.showNotifications = false;
    this.loadConfig();
  }

  closeConfigModal() {
    this.showConfigModal = false;
    this.cdr.detectChanges();
  }

  loadConfig() {
    this.loadingConfig = true;
    this.cdr.detectChanges();
    this.metaNotificationConfigService
      .getConfig({
        "x-api-requestId": uuidv4(),
        "x-api-canal": "web",
      })
      .subscribe({
        next: (response) => {
          if (response.body) {
            this.configEnabledTypes = new Set(
              (response.body.enabledTypes || []) as NotificationType[],
            );
          }
          this.loadingConfig = false;
          this.cdr.detectChanges();
        },
        error: () => {
          this.loadingConfig = false;
          this.cdr.detectChanges();
        },
      });
  }

  isTypeEnabled(type: NotificationType): boolean {
    return this.configEnabledTypes.has(type);
  }

  toggleType(type: NotificationType) {
    if (this.configEnabledTypes.has(type)) {
      this.configEnabledTypes.delete(type);
    } else {
      this.configEnabledTypes.add(type);
    }
    this.cdr.detectChanges();
  }

  saveConfig() {
    if (this.savingConfig) return;
    this.loaderService.show();
    this.cdr.detectChanges();
    const params: UpdateConfig$Params = {
      "x-api-requestId": uuidv4(),
      "x-api-canal": "web",
      body: { enabledTypes: Array.from(this.configEnabledTypes) as any },
    };
    this.metaNotificationConfigService.updateConfig(params).subscribe({
      next: () => {
        this.savingConfig = false;
        this.showConfigModal = false;
        this.cdr.detectChanges();
        this.loaderService.hide();
      },
      error: () => {
        this.savingConfig = false;
        this.cdr.detectChanges();
        this.loaderService.hide();
      },
    });
  }

  refreshNotifications() {
    if (!this.canUseNotifications()) return;
    const totalSize = (this.page + 1) * this.size;

    this.metaNotificationService
      .getNotifications({
        "x-api-requestId": uuidv4(),
        "x-api-canal": "web",
        page: 0,
        size: totalSize,
        unreadOnly: this.unreadOnly,
      })
      .subscribe({
        next: (response) => {
          if (response.body && response.body.notifications) {
            const entities = this.sortNotifications(
              response.body.notifications.entities || [],
            );
            this.notifyCompletedTasks(entities);
            this.processNotifications(entities);
            this.notifications = entities;
            this.totalElements = response.body.notifications.totalElements || 0;
            this.unreadCount = response.body.unreadCount || 0;
            this.actionRequiredCount = response.body.actionRequiredCount || 0;
            this.paymentAction = response.body.paymentAction;
            this.cdr.detectChanges();
          }
        },
        error: () => {},
      });
  }

  @HostListener("document:click", ["$event"])
  onDocumentClick(event: MouseEvent) {
    const target = event.target as HTMLElement;
    const clickedInsideDropdown = target.closest(".notification-dropdown");
    const clickedToggleButton = target.closest(".notification-toggle-btn");
    const clickedToolbarMenu = target.closest(".header-toolbar-menu");
    const clickedToolbarTrigger = target.closest(".header-toolbar-trigger");
    if (!clickedInsideDropdown && !clickedToggleButton) {
      this.showNotifications = false;
    }
    if (!clickedToolbarMenu && !clickedToolbarTrigger) {
      this.closeToolbarMenus();
    }
    this.cdr.detectChanges();
  }

  @HostListener("window:dashy-user-menu-opened")
  onUserMenuOpened() {
    this.closeToolbarMenus();
    this.showNotifications = false;
    this.cdr.detectChanges();
  }

  @HostListener("window:resize")
  onWindowResize() {
    if (this.showNotifications) {
      this.updateNotificationDropdownPosition();
    }
  }

  ngOnDestroy() {
    this.stopPolling();
    this.routerSubscription?.unsubscribe();
    this.logoSubscription?.unsubscribe();
    if (this.ownedLogoUrl) {
      URL.revokeObjectURL(this.ownedLogoUrl);
    }
    this.objectUrls.forEach((url) => URL.revokeObjectURL(url));
  }
}
